import { useEffect, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

type Subnet = { id: string; name: string; cidrBlock: string };

export function OciSubnetPickerComponent({
  onChange,
  rawErrors,
  formData,
  formContext,
}: any) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const fd = (formContext as any)?.formData ?? {};
  const region: string = fd.region ?? '';
  const compartmentId: string = fd.compartmentId ?? '';
  const vcnId: string = fd.vpcId ?? '';
  const [subnets, setSubnets] = useState<Subnet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!region || !compartmentId || !vcnId) {
      setSubnets([]);
      onChange(undefined as any);
      return;
    }
    setLoading(true);
    setError('');
    discoveryApi.getBaseUrl('infrastructure').then(baseUrl => {
      fetchApi
        .fetch(
          `${baseUrl}/oci/subnets?region=${encodeURIComponent(
            region,
          )}&compartmentId=${encodeURIComponent(
            compartmentId,
          )}&vcnId=${encodeURIComponent(vcnId)}`,
        )
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setError(
              typeof data.error === 'string'
                ? data.error
                : data.error?.message ?? 'Failed to load subnets',
            );
          } else {
            setSubnets(data.subnets ?? []);
            onChange(undefined as any);
          }
        })
        .catch(() => setError('Failed to load subnets'))
        .finally(() => setLoading(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, compartmentId, vcnId]);

  if (loading) return <CircularProgress size={20} />;

  return (
    <FormControl fullWidth error={!!rawErrors?.length || !!error}>
      <InputLabel>Subnet</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={!region || !compartmentId || !vcnId}
      >
        {(!region || !compartmentId || !vcnId) && (
          <MenuItem value="" disabled>
            Select region, compartment and VCN first
          </MenuItem>
        )}
        {subnets.map(s => (
          <MenuItem key={s.id} value={s.id}>
            {s.name} ({s.cidrBlock})
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}

export const OciSubnetPickerField = createFormField({
  name: 'OciSubnetPicker',
  component: OciSubnetPickerComponent,
});
